// Tracks: defined by control points (x, z, altitude), sampled every metre.
import { BRAKE, cornerSpeed, laneFactor } from './physics.js';

export const LANE_W = 2.4;
/**
 * Sideways limit of a rider's position (road units of LANE_W, either side of the centre line): about 4.1 m, a hand short
 * of the 4.8 m edge. Nothing but this limit bounds a position: there are no lanes on the road, anyone rides anywhere.
 */
export const ROAD_EDGE = 1.7;
/** Top sideways speed (units per second): about 1.6 m/s, a determined move. An instant swerve would shed any wheel for free. */
export const LANE_SPEED = 0.65;
/**
 * A rider steers a bicycle, not a cursor: the sideways speed builds up and dies away at a bounded acceleration, and it
 * scales with how far the aim is. Small corrections are slow, a real move goes up to LANE_SPEED.
 * `calm`: speed of the smallest correction. `gain`: speed per unit of distance to the aim (`botGain`: a bot in no hurry). `accel`: units/s².
 */
export const LATERAL = { calm: 0.05, gain: 1.6, botGain: 0.6, accel: 1.2, angle: { bot: 0.08, human: 0.15 }, floor: 0.06 };
/**
 * Fastest a rider of speed `v` (m/s) moves across the road, in units/s. A bicycle's path turns by a few degrees, not across
 * the road: about 4.5 degrees for a bot, 8.5 for a human (`LATERAL.angle`, tangents), whatever the speed; on a climb a
 * move simply takes longer. `floor`: a rider at walking pace can still move over.
 */
export const lateralLimit = (v: number, human: boolean): number => Math.min(LANE_SPEED, Math.max(LATERAL.floor, (v * (human ? LATERAL.angle.human : LATERAL.angle.bot)) / LANE_W));
/** One step of a rider's sideways position towards `aim`: the new position and sideways speed. */
export function lateralStep(lane: number, speed: number, aim: number, dt: number, gain = LATERAL.gain, top = LANE_SPEED): { lane: number; speed: number } {
  const err = aim - lane, want = Math.sign(err) * Math.min(top, LATERAL.calm + Math.abs(err) * gain) * Math.min(1, Math.abs(err) / 0.02);
  // never faster than what can still be stopped by the aim at this acceleration: the rider settles, with no overshoot
  const stop = Math.sqrt(2 * LATERAL.accel * Math.abs(err)), limited = Math.sign(want) * Math.min(Math.abs(want), stop);
  const dv = limited - speed, next = speed + Math.sign(dv) * Math.min(Math.abs(dv), LATERAL.accel * dt);
  if (Math.abs(err) < 1e-3 && Math.abs(next) < 0.02) return { lane: aim, speed: 0 };
  return { lane: lane + next * dt, speed: next };
}

/**
 * Track: control points [x, z, altitude] in metres, closed into a loop, or with `open` a road from the first point to the
 * last (ADR 0029). No `id`: identifier derived from the points.
 */
export interface TrackDef {
  id?: string; name: string; desc?: string; level?: number; pts: number[][];
  /** Real altitude of the lowest point (m): air density. Absent: sea level. */
  alt0?: number;
  /** A point-to-point road: the start line on the first point, the finish on the last, ridden once. */
  open?: boolean;
}
/**
 * A sampled track. Positions along it (`s`, m) wrap modulo `L`, the period of the arrays. On a loop `lap` is `L`. On an
 * open road the arrays hold the course (`0` to `lap`), then a run-out of `run` m past the finish, then a lead-in of
 * `lead` m before the start line: the negative positions of the grid wrap onto it, so every lookup stays a modulo, and
 * nobody rides from the run-out into the lead-in (the run-out's speed limit falls to a stop before its end).
 */
export interface Track {
  id: string; name: string; N: number; step: number; L: number;
  x: Float32Array; z: Float32Array; y: Float32Array; tx: Float32Array; tz: Float32Array; grade: Float32Array;
  /** Signed curvature (1/m), positive when the corner's centre is on the side of the positive lanes. */
  curv: Float32Array;
  /** Allowed speed (m/s): corner limit, lowered upstream to leave braking distance. */
  vmax: Float32Array;
  /** Real altitude of the lowest point (m). */
  alt0: number;
  /** Climbing of one lap, or of the course from start to finish. */
  climb: number; boxRows: number[];
  open: boolean;
  /** Distance from the start line to the finish: one lap. */
  lap: number;
  /** Open road: road kept before the start line and after the finish (m); 0 on a loop. */
  lead: number; run: number;
}
export interface TrackPoint { x: number; y: number; z: number; tx: number; tz: number; grade: number }

export const TRACKS: Record<string, TrackDef> = {
  collines: {
    name: 'Hills Circuit',
    desc: '2.7 km · a 7% climb then a false flat',
    level: 1,
    pts: [[0, 0, 0], [250, -20, 2], [480, 40, 10], [620, 200, 26], [600, 420, 34], [430, 560, 24], [200, 580, 12], [20, 480, 6], [-150, 560, 14], [-330, 450, 22], [-380, 230, 12], [-250, 60, 4]],
  },
  mur: {
    name: 'Wall Loop',
    desc: '2.1 km · fast and flat with a short 6% wall',
    level: 1,
    pts: [[0, 0, 0], [300, 0, 0], [480, 80, 1], [540, 250, 2], [460, 400, 3], [280, 440, 14], [100, 420, 14], [-80, 400, 4], [-220, 300, 1], [-250, 130, 0], [-160, 20, 0]],
  },
  nuages: {
    name: 'Cloud Pass',
    desc: '3.2 km · a real 90 m climb, gradients up to 10%',
    level: 3,
    pts: [[0, 0, 0], [300, 0, 4], [600, 80, 20], [800, 250, 42], [790, 480, 64], [600, 620, 86], [350, 650, 92], [100, 700, 82], [-150, 650, 66], [-350, 520, 48], [-420, 320, 30], [-360, 140, 14], [-200, 30, 4]],
  },
  crit: {
    name: 'City Criterium',
    desc: '1.6 km · flat, tight corners, perfect for sprinters',
    level: 6,
        // the point at 80 m rounds the last corner: without it the spline makes a 6 m radius bend before the line
    pts: [[0, 0, 0], [80, 0, 0], [350, 0, 0], [448, 42, 0], [476, 168, 1], [448, 308, 1], [336, 364, 0], [84, 364, 0], [-28, 308, 0], [-56, 168, 0], [-40, 40, 0]],
  },
  // Laid out from road-book segments (length, turn, gradient), then sampled into control points.
  // Open roads (ADR 0029): the start line on the first point, the finish on the last.
  coast: {
    name: 'Coast Road',
    desc: '5.0 km · flat by the sea, long straights for a big bunch',
    level: 2,
    pts: [[0, 0, 1.4], [204, -19, 1.1], [408, -39, 0.7], [621, -59, 0.3], [825, -78, 0], [994, 1, 0.7], [1030, 194, 2.1], [1036, 393, 4], [1028, 593, 5.8], [1039, 801, 9.8], [1110, 983, 13.2], [1156, 1173, 10.7], [1112, 1369, 8.1], [911, 1446, 5.6], [679, 1385, 5], [438, 1312, 4.7], [210, 1234, 4.3], [-16, 1148, 4], [-228, 1034, 2.7], [-275, 809, 0.3], [-235, 577, 1.1], [-228, 328, 1.9], [-179, 105, 1.7]],
  },
  ardennes: {
    name: 'Ardennes Loop',
    desc: '4.6 km · two short climbs, the second a 10% wall',
    level: 4,
    pts: [[0, 0, 2.4], [196, -20, 1.2], [393, -45, 0], [544, 44, 0.8], [575, 223, 12.1], [643, 391, 26.9], [747, 542, 38.7], [769, 719, 41.5], [690, 886, 32.3], [582, 1039, 19.1], [419, 1131, 14.9], [233, 1047, 14.2], [50, 941, 15], [-148, 885, 24.8], [-343, 918, 43.7], [-539, 899, 48.5], [-688, 745, 41.3], [-795, 557, 30.1], [-850, 348, 20.4], [-770, 155, 15.2], [-588, 71, 10.5], [-393, 30, 6.3], [-196, 15, 3.6]],
  },
  forest: {
    name: 'Forest Rollers',
    desc: '3.4 km · rolling roads under the trees, never flat for long',
    level: 5,
    pts: [[0, 0, 0], [161, -9, 0.1], [322, -9, 1.9], [444, 88, 7.4], [550, 219, 14.9], [691, 292, 18], [784, 421, 12.6], [766, 588, 5.6], [636, 717, 1.9], [521, 859, 7.4], [380, 988, 16.5], [185, 979, 22.1], [18, 873, 18.5], [-133, 743, 10.2], [-312, 654, 4], [-416, 494, 7.7], [-376, 307, 8.2], [-285, 155, 6.5], [-157, 44, 3.5]],
  },
  valley: {
    name: 'Valley Road',
    desc: '6.9 km from A to B · down the valley, a short climb, a sprint in town',
    level: 3,
    open: true,
    // downhill overall: 31 m lower at the finish
    pts: [[0, 0, 31], [200, 0, 29], [390, 0, 27.1], [590, 0, 25.1], [788, 25, 21.2], [973, 100, 17.2], [1142, 186, 14.3], [1329, 256, 11.3], [1523, 304, 8.3], [1705, 358, 8], [1874, 463, 8], [2013, 606, 10.8], [2173, 726, 18.8], [2347, 802, 27], [2527, 888, 37], [2709, 970, 34.2], [2896, 1001, 28.5], [3094, 981, 23], [3293, 962, 19], [3493, 962, 15], [3682, 981, 11.2], [3881, 1006, 8.6], [4080, 998, 6.6], [4266, 959, 4.7], [4459, 907, 4], [4657, 920, 4], [4840, 998, 3.9], [5010, 1083, 2.9], [5196, 1158, 1.9], [5387, 1216, 0.9], [5573, 1255, 0], [5768, 1297, 0], [5961, 1349, 0], [6151, 1413, 0], [6329, 1478, 0], [6517, 1546, 0]],
  },
  summit: {
    name: 'Summit Road',
    desc: '6.5 km from A to B · 440 m up through four hairpins, finish at the top',
    level: 7,
    open: true,
    alt0: 1300,
    // the climb starts after the first kilometre
    pts: [[0, 0, 0], [100, 2, 2], [200, 7, 4], [299, 16, 6], [399, 29, 8], [497, 44, 10], [596, 58, 15], [686, 65, 19.5], [786, 66, 24.5], [886, 61, 29.5], [986, 53, 36.8], [1085, 44, 44.3], [1185, 35, 51.8], [1284, 26, 59.3], [1384, 18, 66.8], [1484, 9, 74.3], [1583, 0, 81.8], [1683, -8, 89.3], [1764, -59, 95.4], [1761, -154, 101.4], [1686, -199, 106.8], [1587, -191, 114.4], [1487, -183, 122.4], [1387, -174, 130.4], [1288, -165, 138.4], [1188, -157, 146.4], [1088, -148, 154.4], [989, -139, 162.4], [889, -130, 170.4], [797, -158, 176.8], [774, -251, 182.8], [843, -317, 188.8], [942, -326, 196.8], [1042, -334, 205.3], [1132, -339, 213], [1232, -344, 221.5], [1332, -348, 229.9], [1432, -351, 238.4], [1532, -353, 246.9], [1632, -354, 255.4], [1724, -382, 262.2], [1749, -474, 268.2], [1681, -542, 274.2], [1582, -545, 281.6], [1482, -543, 289.6], [1382, -541, 297.6], [1282, -537, 305.6], [1192, -533, 312.8], [1092, -528, 320.8], [992, -521, 328.8], [893, -513, 336.8], [796, -526, 343.6], [754, -612, 349.6], [807, -691, 355.6], [906, -705, 363.1], [1006, -708, 371.6], [1106, -706, 380.1], [1206, -699, 388.6], [1305, -687, 397.1], [1403, -670, 405.6], [1491, -651, 413.3], [1589, -628, 420.5], [1688, -620, 426.5], [1788, -629, 432.5], [1885, -654, 437], [1981, -680, 440], [2078, -705, 443]],
  },
};

export const mod = (a: number, n: number): number => ((a % n) + n) % n;

function catmull(p0: number[], p1: number[], p2: number[], p3: number[], t: number): number[] {
  const t2 = t * t, t3 = t2 * t;
  return p1.map((_, k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3));
}

const cache = new Map<string, Track>();
const CACHE_MAX = 24;

/** Short, stable fingerprint of the points (identifier of a created track without an id). */
export function trackKey(pts: number[][]): string {
  let h = 0x811c9dc5;
  for (const p of pts) for (const v of p) { h ^= Math.round(v * 10); h = Math.imul(h, 0x01000193); }
  return 'c' + (h >>> 0).toString(36);
}

/** Road an open road keeps before its start line (the grid, a rolling start, team waves) and after its finish (m). */
export const OPEN_ROAD = { lead: 400, run: 400 } as const;

/** Builds (and caches) a sampled track: positions, tangents, smoothed gradients. */
export function buildTrack(src: string | TrackDef): Track {
  const def = typeof src === 'string' ? TRACKS[src] || TRACKS.collines : src;
  const open = !!def.open;
  const id = typeof src === 'string' ? (TRACKS[src] ? src : 'collines') : src.id || trackKey(src.pts) + (open ? 'o' : '');
  const key = typeof src === 'string' ? id : `${id}|${trackKey(src.pts)}|${src.alt0 ?? 0}|${open ? 'o' : 'c'}`; // two versions of a creation do not share the cache
  const hit = cache.get(key);
  if (hit) return hit;
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value!);
  const tr = open ? buildOpen(def, id) : buildLoop(def, id);
  cache.set(key, tr);
  return tr;
}

/** Spline through the control points, 120 samples per span; a loop's last span returns to the first point. */
function densePoints(P: number[][], open: boolean): number[][] {
  const n = P.length, at = (i: number) => (open ? P[Math.max(0, Math.min(n - 1, i))] : P[mod(i, n)]), dense: number[][] = [];
  for (let i = 0; i < (open ? n - 1 : n); i++) for (let k = 0; k < 120; k++) dense.push(catmull(at(i - 1), at(i), at(i + 1), at(i + 2), k / 120));
  if (open) dense.push([...P[n - 1]]);
  return dense;
}

/** Resamples a polyline every `step` m from its start: `count` points (x, z, altitude). */
function resample(dense: number[][], cum: number[], step: number, count: number, closed: boolean) {
  const x = new Float32Array(count), z = new Float32Array(count), y = new Float32Array(count);
  let j = 0;
  for (let i = 0; i < count; i++) {
    const s = i * step;
    while (j < cum.length - 2 && cum[j + 1] < s) j++;
    const f = Math.min(1, (s - cum[j]) / (cum[j + 1] - cum[j] || 1)), a = dense[j], b = dense[closed ? (j + 1) % dense.length : j + 1];
    x[i] = a[0] + (b[0] - a[0]) * f; z[i] = a[1] + (b[1] - a[1]) * f; y[i] = a[2] + (b[2] - a[2]) * f;
  }
  return { x, z, y };
}

/**
 * What follows from the positions: smoothed altitude, tangents, gradients, curvature. `nb` maps a neighbour's index
 * to a sample: around a loop, or held at the ends of an open road.
 */
function derive(x: Float32Array, z: Float32Array, y: Float32Array, step: number, nb: (i: number) => number) {
  const N = x.length, ys = new Float32Array(N), W = 25;
  for (let i = 0; i < N; i++) { let acc = 0; for (let k = -W; k <= W; k++) acc += y[nb(i + k)]; ys[i] = acc / (2 * W + 1); }
  const tx = new Float32Array(N), tz = new Float32Array(N), grade = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const a = nb(i - 3), b = nb(i + 3), dx = x[b] - x[a], dz = z[b] - z[a], h = Math.hypot(dx, dz) || 1;
    tx[i] = dx / h; tz[i] = dz / h; grade[i] = (ys[b] - ys[a]) / h;
  }
  return { ys, tx, tz, grade, curv: curvature(tx, tz, step, nb) };
}

function buildLoop(def: TrackDef, id: string): Track {
  const dense = densePoints(def.pts, false);
  const cum = [0];
  for (let i = 1; i <= dense.length; i++) {
    const a = dense[i - 1], b = dense[i % dense.length];
    cum.push(cum[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = cum[cum.length - 1], N = Math.round(total), step = total / N;
  const { x, z, y } = resample(dense, cum, step, N, true);
  const { ys, tx, tz, grade, curv } = derive(x, z, y, step, i => mod(i, N));
  let climb = 0;
  for (let i = 0; i < N; i++) climb += Math.max(0, ys[(i + 1) % N] - ys[i]);
  const L = N * step;
  return { id, name: def.name, N, step, L, x, z, y: ys, tx, tz, grade, curv, vmax: speedLimits(curv, step), alt0: def.alt0 ?? 0, climb, boxRows: boxRowsOf(L), open: false, lap: L, lead: 0, run: 0 };
}

/**
 * An open road, built in riding order (lead-in, course, run-out; the lead-in and run-out are straight roads in line
 * with each end), then rotated so that index 0 is the start line and the lead-in sits at the end of the arrays.
 */
function buildOpen(def: TrackDef, id: string): Track {
  const dense = densePoints(def.pts, true);
  const cum = [0];
  for (let i = 1; i < dense.length; i++) cum.push(cum[i - 1] + Math.hypot(dense[i][0] - dense[i - 1][0], dense[i][1] - dense[i - 1][1]));
  const total = cum[cum.length - 1], Nc = Math.max(2, Math.round(total)), step = total / Nc;
  const Nl = Math.round(OPEN_ROAD.lead / step), Nr = Math.round(OPEN_ROAD.run / step), M = Nl + Nc + 1 + Nr;
  const course = resample(dense, cum, step, Nc + 1, false);
  // the direction of each end, over its last few metres: the lead-in and the run-out carry on the road without a kink
  const dir = (a: number[], b: number[]) => { const h = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [(b[0] - a[0]) / h, (b[1] - a[1]) / h]; };
  const k = Math.min(dense.length - 1, 4), [sx, sz] = dir(dense[0], dense[k]), [ex, ez] = dir(dense[dense.length - 1 - k], dense[dense.length - 1]);
  const ux = new Float32Array(M), uz = new Float32Array(M), uy = new Float32Array(M);
  for (let i = 0; i < Nl; i++) { const back = (Nl - i) * step; ux[i] = course.x[0] - sx * back; uz[i] = course.z[0] - sz * back; uy[i] = course.y[0]; }
  ux.set(course.x, Nl); uz.set(course.z, Nl); uy.set(course.y, Nl);
  for (let j = 1; j <= Nr; j++) { const i = Nl + Nc + j; ux[i] = course.x[Nc] + ex * j * step; uz[i] = course.z[Nc] + ez * j * step; uy[i] = course.y[Nc]; }
  const u = derive(ux, uz, uy, step, i => Math.max(0, Math.min(M - 1, i)));
  // speed limits in riding order, down to a stop at the end of the run-out
  const v = new Float32Array(M);
  for (let i = 0; i < M; i++) v[i] = cornerSpeed(Math.abs(u.curv[i]) > 1e-6 ? 1 / Math.abs(u.curv[i]) : Infinity);
  v[M - 1] = 0;
  for (let i = M - 2; i >= 0; i--) v[i] = Math.min(v[i], Math.sqrt(v[i + 1] * v[i + 1] + 2 * BRAKE * step));
  let climb = 0;
  for (let i = Nl; i < Nl + Nc; i++) climb += Math.max(0, u.ys[i + 1] - u.ys[i]);
  const rot = (a: Float32Array) => { const o = new Float32Array(M); o.set(a.subarray(Nl), 0); o.set(a.subarray(0, Nl), M - Nl); return o; };
  const lap = Nc * step;
  return {
    id, name: def.name, N: M, step, L: M * step, x: rot(ux), z: rot(uz), y: rot(u.ys), tx: rot(u.tx), tz: rot(u.tz), grade: rot(u.grade), curv: rot(u.curv), vmax: rot(v),
    alt0: def.alt0 ?? 0, climb, boxRows: boxRowsOf(lap), open: true, lap, lead: Nl * step, run: Nr * step,
  };
}

const boxRowsOf = (lap: number) => [0.12, 0.42, 0.72].map(f => Math.round(f * lap));

/** Signed curvature measured between tangents ±4 steps apart (smooths out small sampling glitches). */
function curvature(tx: Float32Array, tz: Float32Array, step: number, nb: (i: number) => number): Float32Array {
  const N = tx.length, c = new Float32Array(N), K = 4;
  for (let i = 0; i < N; i++) {
    const a = nb(i - K), b = nb(i + K);
    c[i] = Math.atan2(tx[a] * tz[b] - tz[a] * tx[b], tx[a] * tx[b] + tz[a] * tz[b]) / ((mod(b - a, N) || 1) * step);
  }
  return c;
}

/** Allowed speed with braking anticipation, around a loop. */
function speedLimits(curv: Float32Array, step: number): Float32Array {
  const N = curv.length, v = new Float32Array(N);
  for (let i = 0; i < N; i++) v[i] = cornerSpeed(Math.abs(curv[i]) > 1e-6 ? 1 / Math.abs(curv[i]) : Infinity);
  // backward pass, two laps to cross the line: v² ≤ v_next² + 2·BRAKE·step
  for (let n = 2 * N - 1; n >= 0; n--) {
    const i = n % N, next = v[(i + 1) % N];
    v[i] = Math.min(v[i], Math.sqrt(next * next + 2 * BRAKE * step));
  }
  return v;
}

/** Stretch of road that exists, as positions: a whole lap, or an open road from its lead-in to the end of its run-out. */
export const roadSpan = (tr: Track): [number, number] => (tr.open ? [-tr.lead, tr.lap + tr.run] : [0, tr.L]);

/** Signed distance from `from` to `to` along the road: the shorter way round a loop, plain on an open road. */
export const trackGap = (tr: Track, from: number, to: number): number => (tr.open ? to - from : mod(to - from + tr.L / 2, tr.L) - tr.L / 2);

/** Samples of the course itself (start line to finish): what a profile, the facts or a photo look at. */
export const courseSamples = (tr: Track): number => (tr.open ? Math.round(tr.lap / tr.step) + 1 : tr.N);

/** Allowed speed (m/s) at distance s: the lower of the two surrounding samples. On a lane shifted
 * by `offset` m, the radius changes: the limit follows the square root of the chord factor. */
export function speedLimitAt(tr: Track, s: number, offset = 0): number {
  const i = Math.floor(mod(s, tr.L) / tr.step) % tr.N;
  const v = Math.min(tr.vmax[i], tr.vmax[(i + 1) % tr.N]);
  return offset ? v * Math.sqrt(laneFactor(tr.curv[i], offset)) : v;
}

/** Chord factor at distance s for a lane shifted by `offset` m (see laneFactor). */
export function laneFactorAt(tr: Track, s: number, offset: number): number {
  return laneFactor(tr.curv[Math.floor(mod(s, tr.L) / tr.step) % tr.N], offset);
}

/** Position, tangent and gradient at distance s (m), shifted sideways by `lane` lanes. */
export function trackAt(tr: Track, s: number, lane = 0): TrackPoint {
  const u = mod(s, tr.L) / tr.step, i = Math.floor(u) % tr.N, k = (i + 1) % tr.N, f = u - Math.floor(u);
  const lx = tr.x[i] + (tr.x[k] - tr.x[i]) * f, lz = tr.z[i] + (tr.z[k] - tr.z[i]) * f, ly = tr.y[i] + (tr.y[k] - tr.y[i]) * f;
  const tx = tr.tx[i] + (tr.tx[k] - tr.tx[i]) * f, tz = tr.tz[i] + (tr.tz[k] - tr.tz[i]) * f, h = Math.hypot(tx, tz) || 1;
  return { x: lx - (tz / h) * lane * LANE_W, y: ly, z: lz + (tx / h) * lane * LANE_W, tx: tx / h, tz: tz / h, grade: tr.grade[i] + (tr.grade[k] - tr.grade[i]) * f };
}

export const altitudeAt = (tr: Track, s: number): number => trackAt(tr, s).y;

/** Key figures of a track (cards, previews, validation). */
export function trackFacts(tr: Track) {
  let maxGrade = 0, minGrade = 0;
  for (let i = 0, n = courseSamples(tr); i < n; i++) { if (tr.grade[i] > maxGrade) maxGrade = tr.grade[i]; if (tr.grade[i] < minGrade) minGrade = tr.grade[i]; }
  return { length: tr.lap, climb: tr.climb, maxGrade, minGrade, open: tr.open };
}

// ---------- Tracks described by segments ----------
// A human (or the AI) thinks in lengths, corners and gradients, not coordinates. The course is built
// turtle-style then closed: the heading, position and altitude gaps are spread along the route.

/** Road the thumbnail camera needs in sight around its spot (m): it hovers up to ~34 m behind the pack and frames ahead. */
const SPOT_BEHIND = 40, SPOT_AHEAD = 30;

/**
 * Most photogenic distance along a track, for its thumbnail: the heart of the steepest climb (gradient smoothed over
 * about forty metres), or failing that the tightest corner. The start arch and the finish line are avoided, and so is
 * any road `hidden` reports (a tunnel: the camera would sit inside the hill raised over it), with some margin around.
 * If every candidate is hidden, the spot falls back to the unrestricted choice.
 */
export function scenicSpot(tr: Track, hidden?: (s: number) => boolean): number {
  const w = Math.max(1, Math.round(20 / tr.step));
  const blocked = hidden ? Array.from({ length: tr.N }, (_, i) => hidden(i * tr.step)) : null;
  const before = Math.ceil(SPOT_BEHIND / tr.step), after = Math.ceil(SPOT_AHEAD / tr.step);
  const clear = (i: number) => {
    for (let k = -before; k <= after; k++) if (blocked?.[mod(i + k, tr.N)]) return false;
    return true;
  };
  const pick = (open: (i: number) => boolean): number => {
    let climb = -Infinity, climbS = -1, bend = -Infinity, bendS = -1;
    for (let i = 0, n = courseSamples(tr); i < n; i++) {
      const s = i * tr.step;
      if (s < 80 || s > tr.lap - 40 || !open(i)) continue;
      let g = 0, c = 0;
      for (let k = -w; k <= w; k++) { const j = mod(i + k, tr.N); g += tr.grade[j]; c += Math.abs(tr.curv[j]); }
      if (g > climb) { climb = g; climbS = s; }
      if (c > bend) { bend = c; bendS = s; }
    }
    if (climbS < 0) return -1;
    return climb / (2 * w + 1) >= 0.025 ? climbS : bendS;
  };
  const s = blocked ? pick(clear) : -1;
  if (s >= 0) return s;
  const any = pick(() => true);
  return any >= 0 ? any : tr.lap * 0.3; // track too short to avoid the start
}

/** Segment: length (m), total turn over the segment (degrees, + to the right), average gradient (%). */
export interface CourseSegment { len: number; turn: number; grade: number }
export interface CourseIssue { level: 'error' | 'warning'; message: string }

const STEP = 20;

/**
 * Control points [x, z, altitude] of a course described by segments: a loop, closed by spreading the heading, position
 * and altitude gaps along it, or with `open` a road from A to B laid out as written (ADR 0029).
 */
export function courseToPoints(segs: CourseSegment[], open = false): number[][] {
  const steps: { d: number; turn: number; dz: number }[] = [];
  for (const g of segs) {
    const n = Math.max(1, Math.round(g.len / STEP));
    for (let i = 0; i < n; i++) steps.push({ d: g.len / n, turn: (g.turn * Math.PI) / 180 / n, dz: ((g.grade / 100) * g.len) / n });
  }
  if (!steps.length) return [];
  const L = steps.reduce((a, s) => a + s.d, 0);
  // heading closure: the sum of turns is brought back to the nearest full turn (a loop only)
  const total = steps.reduce((a, s) => a + s.turn, 0), full = 2 * Math.PI * (Math.round(total / (2 * Math.PI)) || Math.sign(total) || 1);
  const dTurn = open ? 0 : (full - total) / L, dAlt = open ? 0 : -steps.reduce((a, s) => a + s.dz, 0) / L;
  let x = 0, z = 0, alt = 0, h = 0, s = 0;
  const raw: number[][] = [[0, 0, 0, 0]];
  for (const st of steps) {
    h += st.turn + dTurn * st.d;
    x += Math.cos(h) * st.d; z += Math.sin(h) * st.d; alt += st.dz + dAlt * st.d; s += st.d;
    raw.push([x, z, alt, s]);
  }
  const [ex, ez] = open ? [0, 0] : [raw[raw.length - 1][0], raw[raw.length - 1][1]];
  // position closure: offset proportional to the distance covered, then downsampling (~60 m); an open road keeps its end
  const every = Math.max(1, Math.round(60 / STEP)), pts: number[][] = [];
  for (let i = 0; i < raw.length - 1; i += every) { const [px, pz, pa, ps] = raw[i], f = ps / L; pts.push([px - ex * f, pz - ez * f, pa]); }
  if (open) {
    // the last point is the finish, never a few metres after the previous one (the spline would kink there)
    if ((raw.length - 1) % every) pts.pop();
    pts.push(raw[raw.length - 1].slice(0, 3));
  }
  const minAlt = Math.min(...pts.map(p => p[2]));
  return pts.map(p => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10, Math.round((p[2] - minAlt) * 10) / 10]);
}

function segmentsCross(a: number[], b: number[], c: number[], d: number[]): boolean {
  const o = (p: number[], q: number[], r: number[]) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
}

/** Checks on a course: length, gradients, crossings, closing gap (a loop's). Errors block publication. */
export function checkCourse(segs: CourseSegment[], open = false): CourseIssue[] {
  const out: CourseIssue[] = [];
  const L = segs.reduce((a, s) => a + s.len, 0), what = open ? 'Road' : 'Loop';
  if (L < 800) out.push({ level: 'error', message: `${what} too short (${Math.round(L)} m, minimum 800 m)` });
  if (L > 25000) out.push({ level: 'error', message: `${what} too long (${(L / 1000).toFixed(1)} km, maximum 25 km)` });
  const turn = segs.reduce((a, s) => a + s.turn, 0), off = Math.abs(turn - 360 * (Math.round(turn / 360) || 1));
  if (!open && off > 90) out.push({ level: 'warning', message: `The corners add up to ${Math.round(turn)}°: a loop is ±360°, the course is heavily corrected` });
  for (const [i, s] of segs.entries()) if (Math.abs(s.turn) > 0 && s.len / ((Math.abs(s.turn) * Math.PI) / 180) < 12) out.push({ level: 'warning', message: `Segment ${i + 1}: very tight corner (radius < 12 m)` });
  const pts = courseToPoints(segs, open);
  if (pts.length >= 4) {
    // a loop's last span joins its first point; an open road has none
    const spans = open ? pts.length - 1 : pts.length;
    for (let i = 0; i < spans; i++) for (let j = i + 2; j < spans; j++) {
      if (!open && i === 0 && j === pts.length - 1) continue;
      if (segmentsCross(pts[i], pts[(i + 1) % pts.length], pts[j], pts[(j + 1) % pts.length])) { out.push({ level: 'error', message: 'The course crosses itself: adjust the corners or the lengths' }); i = pts.length; break; }
    }
    const tr = buildTrack({ name: 'check', pts, open }), f = trackFacts(tr);
    if (f.maxGrade > 0.2) out.push({ level: 'error', message: `Maximum gradient of ${(f.maxGrade * 100).toFixed(1)}%: 20% at most` });
    if (f.minGrade < -0.2) out.push({ level: 'error', message: `Descent of ${(-f.minGrade * 100).toFixed(1)}%: 20% at most` });
  }
  return out;
}
